import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ListPager, { LIST_PAGE_SIZE } from '@client/components/ListPager'
import { useTranslation } from 'react-i18next'
import { Container, SortType } from 'rsuite'
import ListMenu from '@client/components/ListMenu'
import { ProjectIcon } from '@client/config'
import { Button, Table } from '@client/components/Controls'
import EditProject from './EditProject'
import useQuery from '@client/utils/useQuery'
import ProjectModel, { EProjectStatus, TProject } from '@client/models/project'
import { Capabilities, hasPermission } from '@/shared'
import { useLayout, TContextMenuItem } from '@client/components/Layout/LayoutManager'
import { SidebarTopProps } from '@client/stores/tabsStore'
import { useContextMenuTable, ContextTarget } from '@/client/utils/useContextMenu'
import { createDefaultListContextMenuItems } from '@/client/utils/listContextMenuDefaults'
import useSidebarQueryList from '@client/utils/useSidebarQueryList.ts'
import useSidebarInvalidation from '@client/utils/useSidebarInvalidation.ts'

//console.log('Load ProjectLists ...')

const projectModel = new ProjectModel()

export default function SidebarProjects() {
    const { t } = useTranslation()
    const [sortColumn, setSortColumn] = useState<string>('name');
    const [sortType, setSortType] = useState<SortType | undefined>('asc');
    const [page, setPage] = useState<number>(1)
    // archived projects only behind the "Archive" switch, and then only they
    const [showArchive, setShowArchive] = useState<boolean>(false)
    const { addTab, onContextMenu, openTabToRight, removeTabCopies, removeOtherTabs } = useLayout()
    const { rows, loading, error, data, refetch } = useSidebarQueryList<TProject, { page: number; count: number, sortField: string, sortOrder: SortType, archived?: string }>({
        queryFn: projectModel.list,
        args: { page, count: LIST_PAGE_SIZE, sortField: sortColumn, sortOrder: sortType || 'asc', archived: showArchive ? '1' : undefined },
    })

    useSidebarInvalidation({
        tables: ['project'],
        actions: ['updated', 'deleted'],
        refetch,
    })

    const canSort = rows.length > 0

    const controller = {
        defaultRow(params: any) {
            return {
                _id: '',
                name: '',
                clients: ['Standard'],
                emptyNull: true,
            }
        },

        getTabContent(params: any) {
            const { tab } = params
            return <EditProject tabId={tab.id} onCreated={handleProjectCreated} />
        },

        useProjectInBreadCrumb(params: any) {
            return false
        },

        getBreadCrumb(params: any) {
            const { tab } = params
            const breadCrumb: string[] = [t('Projects'), tab.name]
            return breadCrumb
        },

        ignoreProjectExists(params: any) {
            return true
        },
    }

    const handleProjectCreated = (newProject: any) => {
        refetch()
    }

    function onAddProject(): void {
        const timestamp = new Date()
        onEditProject({
            _id: '',
            name: '',
            clients: ['Standard'],
            description: '',
            status: EProjectStatus.Planned,
            startDate: timestamp,
            createdAt: timestamp,
            uodatedAt: timestamp,
            emptyNull: true,
        } as TProject)
    }

    function newTab(row: TProject) {
        let name
        if (!row._id) {
            name = t('New project')
        } else {
            name = row.name
        }

        const parsedRow = {
            ...row,
            startDate: row.startDate ? new Date(row.startDate) : null,
            endDate: row.endDate ? new Date(row.endDate) : null,
        }

        return {
            dataId: `project-${row._id || 0}`,
            name,
            component: 'project',
            controller,
            icon: <ProjectIcon />,
            store: {
                id: row._id,
                initialData: { ...parsedRow },
                formData: parsedRow,
            },
        }
    }

    function onEditProject(row: TProject) {
        addTab(newTab(row))
    }

    function onSort(column: string, sortOrder?: SortType) {
        setPage(1)
        setSortColumn(column)
        setSortType(sortOrder)
        refetch()
    }

    function onRenderLoading(loading: ReactNode): ReactNode {
        return null
    }

    function onRenderEmpty() {
        return <div className="rs-table-body-info">{t('No projects found.')}</div>
    }

    function SidebarTop({ data }: SidebarTopProps) {
        return (
            <Container className="tree-top">
                {hasPermission(Capabilities.project_write) && (
                    <Button appearance="ghost" onClick={onAddProject}>
                        {t('Add project')}
                    </Button>
                )}
                <ListMenu
                    items={[
                        {
                            label: t('Archive'),
                            checked: showArchive,
                            onClick: () => {
                                setPage(1)
                                setShowArchive(!showArchive)
                                refetch()
                            },
                        },
                    ]}
                />
            </Container>
        )
    }

    const getProjectById = useCallback(
        (id: string): TProject | null => {
            return rows.find((p) => p._id === id) || null
        },
        [rows]
    )

    const baseMenuItems = useMemo<TContextMenuItem[]>(() => {
        return createDefaultListContextMenuItems<TProject>({
            t,
            getItemById: getProjectById,
            addTab,
            openTabToRight,
            createTab: newTab,
            removeTabCopies,
            removeOtherTabs,
        })
    }, [t, getProjectById, addTab, openTabToRight, removeTabCopies, removeOtherTabs])

    const contextMenuItems = baseMenuItems

    const showContextMenu = useCallback(
        (event: React.MouseEvent, target: ContextTarget) => {
            onContextMenu(event, contextMenuItems, {
                id: target.id,
                label: target.label ?? '',
            })
        },
        [onContextMenu, contextMenuItems]
    )

    const handleRowContextMenu = useContextMenuTable<TProject>(showContextMenu, (row) => ({
        id: row._id,
    }))

    return (
        <div className="sidebar-content sidebar-content-projects">
            <SidebarTop data={data} />
            <Table
                className={'tablist-table' + (rows.length == 0 ? ' empty-list' : '')}
                loading={loading}
                wordWrap="break-word"
                fillHeight
                data={rows}
                renderEmpty={onRenderEmpty}
                renderLoading={onRenderLoading}
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={onSort}
                onRowClick={onEditProject}
                onRowContextMenu={handleRowContextMenu}
            >
                <Table.Column align="left" sortable={canSort} flexGrow={1}>
                    <Table.HeaderCell className="name">{t('Project name')}</Table.HeaderCell>
                    <Table.Cell className="name" dataKey="name" />
                </Table.Column>
            </Table>
            <ListPager total={Number(data?.total ?? 0)} page={page} onChangePage={(p) => { setPage(p); refetch() }} disabled={loading} />
        </div>
    )
}
